/**
 * EOL evaluation for the front end. Mirrors libs/PCB.Platform.About/EolCatalog.cs exactly, so the
 * browser and the Function agree on every status. Same rules:
 *   - supported THROUGH the end date; the day itself is ending-soon with 0 days left;
 *   - ending-soon when fewer than `endingSoonDays` (180) days remain;
 *   - a month-only date counts to the last day of that month;
 *   - no date and no published status -> unknown. Never a guess.
 */

import type { EolCatalogData, EolComponent, EolCycle, EolRow, EolStatusValue, FrontendBuildInfo } from './types';

const DAY_MS = 86_400_000;

/** Days since the epoch for a YYYY-MM-DD string, or null when it is not a real date. */
export function dayNumber(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(t);
  if (d.getUTCFullYear() !== Number(m[1]) || d.getUTCMonth() !== Number(m[2]) - 1 || d.getUTCDate() !== Number(m[3])) return null;
  return Math.round(t / DAY_MS);
}

/** Today as YYYY-MM-DD in UTC (the Function computes in UTC too). */
export function todayIso(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export function lastDayOfMonth(yyyyMm: string): string | null {
  const m = /^(\d{4})-(\d{2})$/.exec(yyyyMm);
  if (!m) return null;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  const last = new Date(Date.UTC(Number(m[1]), month, 0));
  return last.toISOString().slice(0, 10);
}

export function computeStatus(
  supportEnds: string,
  today: string,
  endingSoonDays: number,
): { status: EolStatusValue; daysRemaining: number } {
  const end = dayNumber(supportEnds);
  const now = dayNumber(today);
  if (end === null || now === null) return { status: 'unknown', daysRemaining: NaN };
  const days = end - now;
  if (days < 0) return { status: 'unsupported', daysRemaining: days };
  if (days < endingSoonDays) return { status: 'ending-soon', daysRemaining: days };
  return { status: 'supported', daysRemaining: days };
}

/** "10.0.14-rc.1+abc" -> ["10","0","14"]; leading v / ~ / ^ dropped. */
export function segments(version: string): string[] {
  let s = version.trim().replace(/^[vV~^]+/, '');
  const cut = s.search(/[-+ ]/);
  if (cut >= 0) s = s.slice(0, cut);
  return s
    .split('.')
    .filter((p) => p.length > 0)
    .map((p) => (/^\d+$/.test(p) ? String(Number(p)) : p));
}

export function matchCycle(component: EolComponent, version: string): EolCycle | null {
  const v = segments(version);
  let best: EolCycle | null = null;
  let bestLen = -1;
  for (const cy of component.cycles) {
    const c = segments(cy.cycle);
    if (c.length === 0 || c.length > v.length || c.length <= bestLen) continue;
    if (c.every((seg, i) => seg === v[i])) {
      best = cy;
      bestLen = c.length;
    }
  }
  return best;
}

export function evaluate(catalog: EolCatalogData, component: EolComponent, version: string, today: string): EolRow {
  const cycle = matchCycle(component, version);
  if (!cycle) {
    return {
      component: component.name, version, supportEnds: null, supportEndsMonth: null,
      status: 'unknown', daysRemaining: null, source: null, note: 'This version is not in the catalog.',
    };
  }
  const base = { component: component.name, version, source: cycle.source, note: cycle.note ?? null };
  if (cycle.supportEnds && dayNumber(cycle.supportEnds) !== null) {
    const r = computeStatus(cycle.supportEnds, today, catalog.endingSoonDays);
    return { ...base, supportEnds: cycle.supportEnds, supportEndsMonth: null, ...r };
  }
  if (cycle.supportEndsMonth) {
    const last = lastDayOfMonth(cycle.supportEndsMonth);
    if (last) {
      const r = computeStatus(last, today, catalog.endingSoonDays);
      return { ...base, supportEnds: null, supportEndsMonth: cycle.supportEndsMonth, ...r };
    }
  }
  if (cycle.publishedStatus) {
    return { ...base, supportEnds: null, supportEndsMonth: null, status: cycle.publishedStatus, daysRemaining: null };
  }
  return { ...base, supportEnds: null, supportEndsMonth: null, status: 'unknown', daysRemaining: null };
}

export function componentForPackage(catalog: EolCatalogData, kind: EolComponent['kind'], name: string): EolComponent | null {
  const n = name.toLowerCase();
  return catalog.components.find((c) => c.kind === kind && c.match.some((m) => m.toLowerCase() === n)) ?? null;
}

/** Support status for one front-end package, or null when the catalog doesn't track it. */
export function evaluatePackage(catalog: EolCatalogData, name: string, version: string | null, today: string): EolRow | null {
  if (!version) return null;
  const comp = componentForPackage(catalog, 'npm', name);
  return comp ? evaluate(catalog, comp, version, today) : null;
}

/** EOL rows for the front end: the Node.js that built it, plus every tracked package. */
export function evaluateFrontend(build: FrontendBuildInfo, today: string): EolRow[] {
  const rows: EolRow[] = [];
  const node = build.catalog.components.find((c) => c.id === 'nodejs');
  if (node && build.nodeVersion) rows.push({ ...evaluate(build.catalog, node, build.nodeVersion, today), component: 'Node.js (build)' });
  for (const p of build.packages) {
    const r = evaluatePackage(build.catalog, p.name, p.version, today);
    if (r) rows.push({ ...r, component: r.component.toLowerCase() === p.name.toLowerCase() ? r.component : `${r.component} (${p.name})` });
  }
  return rows;
}
